import { useState, type ReactNode } from 'react';
import { ChevronIcon, InfoIcon, TickIcon, WarnIcon } from './icons';
import { formatMinutes, type TimeFormat } from '../lib/time';

/** Повторяющиеся куски прототипа. Классы и структура — как в `prototype.html`. */

export function Notice({ kind = 'warn', children }: { kind?: 'warn' | 'calm'; children: ReactNode }) {
  return (
    <div className={kind === 'calm' ? 'notice calm' : 'notice'}>
      <span className="ico">{kind === 'calm' ? <InfoIcon /> : <WarnIcon />}</span>
      <span>{children}</span>
    </div>
  );
}

export function Opt({ pressed, disabled, title, aside, note, onClick }: {
  pressed: boolean;
  disabled?: boolean;
  title: string;
  aside?: ReactNode;
  note?: ReactNode;
  onClick: () => void;
}) {
  return (
    <button className="opt" aria-pressed={pressed} disabled={disabled} onClick={onClick}>
      <span className="tick"><TickIcon /></span>
      <span className="opt-body">
        <span className="opt-title">
          <strong>{title}</strong>
          {aside}
        </span>
        {note ? <p>{note}</p> : null}
      </span>
    </button>
  );
}

export function Row({ avatar, title, note, aside, disabled, onClick }: {
  avatar?: ReactNode;
  title: ReactNode;
  note?: ReactNode;
  aside?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      {avatar}
      <span className="rw-body">
        <b>{title}</b>
        {note ? <span>{note}</span> : null}
      </span>
      {aside ?? (onClick ? <span className="chev"><ChevronIcon /></span> : null)}
    </>
  );
  return onClick
    ? <button className="rw" disabled={disabled} onClick={onClick}>{body}</button>
    : <div className="rw">{body}</div>;
}

export function Pill({ kind, children }: { kind: 'paid' | 'await' | 'off' | 'done'; children: ReactNode }) {
  const dot = kind === 'paid' || kind === 'await';
  return (
    <span className={`pill ${kind}`}>
      {dot ? <i className="dot" /> : null}
      {children}
    </span>
  );
}

/**
 * Аватар: фото, если оно есть, иначе инициалы — как в прототипе, две буквы.
 * Битую ссылку прячем и возвращаемся к инициалам: пустой квадрат хуже букв.
 */
export function Avatar({ name, photo, small }: { name: string; photo?: string | null; small?: boolean }) {
  const [broken, setBroken] = useState(false);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.slice(0, 1).toUpperCase())
    .join('');

  const cls = small ? 'avatar sm' : 'avatar';
  if (photo && !broken) {
    return (
      <span className={cls} style={{ padding: 0, overflow: 'hidden' }}>
        <img
          src={photo}
          alt={name}
          width="100%"
          height="100%"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setBroken(true)}
        />
      </span>
    );
  }
  return <span className={cls}>{initials || '—'}</span>;
}


const HOURS = Array.from({ length: 24 }, (_, h) => h);

/** «08» или «8 AM» — подпись часа в выбранном формате. */
function hourLabel(h: number, format: TimeFormat): string {
  if (format === '12') return `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`;
  return String(h).padStart(2, '0');
}

/**
 * Выбор времени двумя селектами: часы и минуты.
 *
 * Нативный `<input type="time">` здесь не годится. Он рисует AM/PM по локали
 * УСТРОЙСТВА, а не по настройке организации: администратор с англоязычным
 * телефоном видел «08:00 PM» там, где центр показывает 20:00. Плюс его выпадашка
 * накрывала соседние строки недели.
 *
 * Селект на телефоне разворачивается родным колесом iOS/Android — попасть пальцем
 * проще, чем в поле ввода, — а подписи мы рисуем сами, поэтому формат всегда тот,
 * что выбрала организация.
 *
 * Значение — минуты от полуночи, как и везде в расписании: строку «ЧЧ:ММ» здесь
 * не собираем, чтобы не гонять данные через формат показа.
 */
export function TimePick({ value, label, format, disabled, step = 5, onChange }: {
  value: number;
  label: string;
  format: TimeFormat;
  disabled?: boolean;
  step?: number;
  onChange: (minutes: number) => void;
}) {
  const h = Math.floor(value / 60);
  const m = value % 60;

  const minutes = Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);
  // Минуту не по шагу (пришла из старых данных) показываем как есть: молча
  // округлив чужое расписание, мы бы сдвинули рабочий день без ведома человека.
  if (!minutes.includes(m)) minutes.push(m);
  minutes.sort((a, b) => a - b);

  return (
    <span className="timepick" title={formatMinutes(value, format)}>
      <select
        aria-label={`${label}, часы`}
        value={h}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value) * 60 + m)}
      >
        {HOURS.map((x) => <option key={x} value={x}>{hourLabel(x, format)}</option>)}
      </select>
      <select
        aria-label={`${label}, минуты`}
        value={m}
        disabled={disabled}
        onChange={(e) => onChange(h * 60 + Number(e.target.value))}
      >
        {minutes.map((x) => <option key={x} value={x}>{String(x).padStart(2, '0')}</option>)}
      </select>
    </span>
  );
}
